import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import LightboxGallery from '../components/LightboxGallery';
import { albums } from '../photographyData';

export default function Photography() {
  const [activeAlbum, setActiveAlbum] = useState(null);
  const [searchParams] = useSearchParams();

  const close = useCallback(() => setActiveAlbum(null), []);

  // Auto-open album from URL param e.g. /photography?album=bts
  useEffect(() => {
    const albumId = searchParams.get('album');
    if (albumId) {
      const found = albums.find((a) => a.id === albumId);
      if (found) setActiveAlbum(found);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Lock scroll when modal is open
  useEffect(() => {
    if (activeAlbum) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
    return () => document.body.classList.remove('overflow-hidden');
  }, [activeAlbum]);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [close]);

  return (
    <>
    <Helmet>
      <title>Cinematic Photography Portfolio | Blackmagic Films — NJ, PA & NYC</title>
      <meta name="description" content="Editorial portraits, concert photography, modeling and brand imagery shot on Blackmagic Cinema Cameras. Serving New Jersey, Philadelphia PA, and New York City." />
      <link rel="canonical" href="https://www.blackmagicfilms.com/photography" />
      <meta property="og:title" content="Cinematic Photography Portfolio | Blackmagic Films" />
      <meta property="og:description" content="Editorial portraits, concert and brand photography shot on high-end cinema cameras across NJ, PA, and NYC." />
      <meta property="og:url" content="https://www.blackmagicfilms.com/photography" />
      <meta property="og:image" content="https://www.blackmagicfilms.com/images/og-image.jpg" />
      <script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.blackmagicfilms.com/" },
          { "@type": "ListItem", "position": 2, "name": "Photography", "item": "https://www.blackmagicfilms.com/photography" }
        ]
      })}</script>
    </Helmet>
    <div className="min-h-screen bg-black text-white pt-16 sm:pt-20">

      {/* Page header */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <span className="text-xs tracking-[0.3em] uppercase text-amber-400 font-medium">
            Black Magic Films
          </span>
          <h1 className="text-4xl sm:text-5xl font-black mt-3 mb-3">Photography</h1>
          <p className="text-neutral-400 max-w-xl">
            Still frames from shoots, sessions, and events — each album a different world.
          </p>
        </motion.div>
      </div>

      {/* Album grid */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-24">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-5">
          {albums.map((album, i) => (
            <motion.button
              key={album.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              onClick={() => setActiveAlbum(album)}
              className="group relative overflow-hidden rounded-2xl ring-1 ring-white/10 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
            >
              {/* Cover image */}
              <div className="aspect-[4/3] w-full overflow-hidden">
                <img
                  src={album.cover}
                  alt={album.title}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                  decoding="async"
                />
              </div>

              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

              {/* Labels */}
              <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6">
                <p className="text-xs tracking-[0.2em] uppercase text-amber-400 font-medium mb-1">
                  {album.subtitle}
                </p>
                <h2 className="text-xl sm:text-2xl font-black text-white leading-tight">
                  {album.title}
                </h2>
                <p className="text-white/50 text-sm mt-1">
                  {album.images.length} {album.images.length === 1 ? 'photo' : 'photos'}
                </p>
              </div>

              {/* Hover CTA */}
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <span className="bg-white/10 backdrop-blur-sm border border-white/20 text-white text-sm font-semibold px-5 py-2.5 rounded-full">
                  View Gallery
                </span>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      {/* Album modal */}
      {activeAlbum && (
        <div
          className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm overflow-y-auto"
          onClick={close}
        >
          <div
            className="min-h-full max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-10"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="flex items-start justify-between mb-6 gap-4">
              <div>
                <p className="text-xs tracking-[0.2em] uppercase text-amber-400 font-medium mb-1">
                  {activeAlbum.subtitle}
                </p>
                <h2 className="text-2xl sm:text-3xl font-black">{activeAlbum.title}</h2>
              </div>
              <button
                onClick={close}
                className="shrink-0 mt-1 rounded-full bg-white/10 hover:bg-white/20 text-white px-4 py-2 text-sm font-semibold transition-colors"
              >
                ✕ Close
              </button>
            </div>

            {/* Gallery — reuse existing lightbox component */}
            <LightboxGallery images={activeAlbum.images} limit={activeAlbum.images.length} />
          </div>
        </div>
      )}
    </div>
    </>
  );
}
